import { useMemo } from 'react';
import { ActionIcon, Anchor, Group, Menu, Text } from '@mantine/core';
import { IconChevronRight, IconFolder } from '@tabler/icons-react';
import type { Folder } from '../../../schemas/folder.schema';
import { folderIconColor, getEffectiveAccess } from './driveAccess';

/**
 * DriveBreadcrumbs — путь до текущей папки Диска (паттерн проводника Windows).
 * Путь строится на клиенте (подъём по parent_id в загруженном списке папок),
 * последний сегмент — текущая папка (не кликабелен). Разделитель «>» —
 * кнопка: выпадающий список подпапок сегмента для быстрого перехода;
 * после последнего сегмента — спуск в его подпапки.
 */
export function DriveBreadcrumbs({
  path,
  folders,
  onNavigate,
}: {
  /** Цепочка папок от корня до текущей (включительно). */
  path: Folder[];
  /** Все видимые папки Диска — для списков подпапок в разделителях. */
  folders: Folder[];
  onNavigate: (folderId: number) => void;
}) {
  const foldersById = useMemo(() => new Map(folders.map(folder => [folder.id, folder])), [folders]);

  if (path.length === 0) return null;

  const childrenOf = (folderId: number): Folder[] =>
    folders
      .filter(folder => folder.parent_id === folderId)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <Group gap={2} wrap='nowrap' style={{ minWidth: 0 }}>
      {path.map((folder, index) => {
        const isLast = index === path.length - 1;
        const children = childrenOf(folder.id);
        /** id следующего сегмента пути — подсветка в списке подпапок */
        const nextInPathId = path[index + 1]?.id;

        return (
          <Group key={folder.id} gap={2} wrap='nowrap' style={{ minWidth: 0 }}>
            {isLast ? (
              <Text size='sm' fw={600} truncate>
                {folder.name}
              </Text>
            ) : (
              <Anchor size='sm' truncate onClick={() => onNavigate(folder.id)}>
                {folder.name}
              </Anchor>
            )}
            {children.length > 0 && (
              <Menu position='bottom-start' withArrow shadow='md'>
                <Menu.Target>
                  <ActionIcon size='xs' variant='subtle' color='gray' title='Подпапки'>
                    <IconChevronRight size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {children.map(child => (
                    <Menu.Item
                      key={child.id}
                      leftSection={
                        <IconFolder
                          size={14}
                          color={folderIconColor(getEffectiveAccess(child, foldersById))}
                        />
                      }
                      fw={child.id === nextInPathId ? 600 : undefined}
                      onClick={() => onNavigate(child.id)}
                    >
                      {child.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        );
      })}
    </Group>
  );
}

/** Построить путь от корня до папки по плоскому списку (parent_id-подъём). */
// buildFolderPath переехала в driveScope.ts — она чистая, и там её тестируют
// без DOM. Реэкспорт не делаем: импортируйте из './driveScope'.
